import { Logo } from '@components/atoms/Logo/Logo';
import { NavItem } from '@components/atoms/NavItem/NavItem';
import { SearchButton } from '@components/atoms/SearchButton/SearchButton';
import { UserMenu } from '@components/molecules/UserMenu/UserMenu';
import AppContext from '@lib/AppContext';
import { IconUser } from '@src/assets/icon-user';
import { GetCustomerDataDocument } from '@src/lib/graphql/generated';
import { useAuthQuery } from '@src/lib/graphql/urql';
import { Button, type PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useContext, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { HeaderNav } from '../../molecules/HeaderNav/HeaderNav';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

const emptyVariables = {};

export interface HeaderProps extends PropsWithClassProps {
  filled?: boolean;
  hideMobileNav?: boolean;
  disableTopSpacing?: boolean;
}

export const Header = ({ filled, hideMobileNav, disableTopSpacing }: HeaderProps) => {
  const { status } = useSession() ?? {};
  const context = useContext(AppContext);
  const mobileSubItems = context.header.navigation?.items.slice(2);
  const router = useRouter();

  // router.asPath differs between server (proxy-rewritten URL) and client (clean browser URL).
  // Defer to client to avoid hydration mismatch in the login callbackUrl.
  const [clientPath, setClientPath] = useState('/');
  useEffect(() => {
    setClientPath(router.asPath);
  }, [router.asPath]);

  const [{ data: customerData }] = useAuthQuery({
    query: GetCustomerDataDocument,
    variables: emptyVariables,
    pause: status !== 'authenticated',
  });

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];

    if (status === 'loading') return;

    // Push outside React's execution context so GTM tag errors
    // (e.g. Customer.io SDK not yet loaded) don't trigger the dev overlay
    setTimeout(() => {
      if (status === 'unauthenticated') {
        window.dataLayer.push({ loginStatus: 'nicht eingeloggt' });
        return;
      }

      if (status === 'authenticated' && customerData?.customerData) {
        window.dataLayer.push({ event: 'metadata', loginStatus: 'eingeloggt' });
        window.dataLayer.push({
          event: 'initiate_client_data',
          customer: {
            firstName: customerData.customerData.firstName ?? '',
            lastName: customerData.customerData.lastName ?? '',
            email: customerData.customerData.email?.email,
            cust_nr_internal: customerData.customerData.customerId,
          },
        });
      }
    });
  }, [status, customerData]);

  return (
    <>
      <header
        className={twMerge(
          'pointer-events-none fixed z-60 grid h-26 w-full content-center items-center gap-x-1.5 px-4 text-white sm:gap-0 lg:h-24 lg:px-12 xl:px-16',
          filled ? 'bg-gray-900' : 'bg-gray-900 lg:bg-gray-900/0',
          hideMobileNav ? 'h-[4.65rem]' : 'h-26',
        )}
      >
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <Logo responsive link classProps={{ root: 'drop-shadow-lg headerLogo' }} />
          <HeaderNav classProps={{ root: 'self-center' }} />
          <div className={'pointer-events-auto z-10 flex items-center justify-end gap-1.5 md:gap-4'}>
            <div className="block">
              <div className="flex h-8 cursor-pointer items-center rounded-full border-0.5 border-gray-650/65 bg-gray-650/65 backdrop-blur-sm md:h-12 lg:bg-gray-800/70">
                <Button
                  classProps={{
                    root: 'hidden px-2 md:px-4 h-full tracking-widest text-[9px] font-bold lg:text-base md:text-sm uppercase font-proxima-nova lg:flex items-center justify-center rounded-full border-0.5 border-transparent transition-colors duration-200 hover:border-tvmedia hover:text-tvmedia headerAbo',
                  }}
                  link="https://www.tv-media.at/abo"
                >
                  Abo
                </Button>
                {process.env.NEXT_PUBLIC_SHOW_LOGIN === 'true' &&
                  (status === 'authenticated' ? (
                    <UserMenu callbackUrl={clientPath} />
                  ) : (
                    <Button
                      classProps={{
                        root: 'px-2 md:px-4 h-full tracking-widest font-bold lg:text-base md:text-sm flex items-center justify-center rounded-full border-0.5 border-transparent transition-colors duration-200 hover:border-tvmedia hover:text-tvmedia',
                      }}
                      link={`/auth/login?callbackUrl=${encodeURIComponent(clientPath)}`}
                    >
                      <IconUser classProps={{ root: 'size-3.5 md:size-4.5' }} color="currentColor" />
                    </Button>
                  ))}
              </div>
            </div>
            <SearchButton />
          </div>
        </div>
        {!hideMobileNav && (
          <nav className="pointer-events-auto flex items-center justify-center gap-4 text-[11px] sm:gap-3 lg:hidden">
            {mobileSubItems?.map((item, index) => (
              <NavItem item={item} key={index} type="sub" />
            ))}
            <NavItem
              item={{ title: 'Abo', url: '/abo', id: 'abo' }}
              key="abo"
              type="sub"
              classProps={{ root: 'pl-3 border-l border-gray-650/75 h-4 leading-none' }}
            />
          </nav>
        )}
      </header>
      {!disableTopSpacing && <div className={twMerge('lg:hidden', hideMobileNav ? 'h-[4.65rem]' : 'h-26')} />}
    </>
  );
};
